import { getRecipeQL } from '@libs/search-service-smart-search';
import type {
  GraphQL,
  ItinerarySearchUserCriteria,
} from '@libs/trip-planner-interfaces';
import {
  IngredientTypeQL,
  SelectedHotelTypeQL,
} from '@libs/trip-planner-interfaces';
import { isDefined } from '@libs/utils';

import type { JSONObject } from './shareWidget.helpers';
import { cleanFalsyValues } from './shareWidget.helpers';

export function getRecipeForShareWidget(
  userCriteria: ItinerarySearchUserCriteria,
  affiliateId: string | undefined,
  shouldCleanRecipe: boolean
): string {
  const recipe = getRecipeQL(
    {
      userCriteria,
      businessCriteria: {
        affiliateId,
      },
    },
    true
  );

  delete recipe.sessionId;
  delete recipe.metadata;

  if (shouldCleanRecipe) {
    return cleanRecipe(recipe);
  }

  recipe.ingredients = recipe.ingredients?.map((ingredient) => {
    if (ingredient?.ingredientType === IngredientTypeQL.Hotel) {
      delete ingredient.numberOfSuggestions;
      if (
        !isDefined(ingredient.selectedHotelTypes) ||
        hasAllHotelTypesSelected(
          ingredient.selectedHotelTypes.filter(isDefined)
        )
      ) {
        ingredient.selectedHotelTypes = [];
      }
    }

    return ingredient;
  });

  return JSON.stringify({
    recipe,
  });
}

function hasAllHotelTypesSelected(
  selectedHotelTypes: SelectedHotelTypeQL[]
): boolean {
  const hotelTypesAmount = 3;
  return (
    selectedHotelTypes.includes(SelectedHotelTypeQL.Hotel) &&
    selectedHotelTypes.includes(SelectedHotelTypeQL.Apartment) &&
    selectedHotelTypes.includes(SelectedHotelTypeQL.HouseOrCottage) &&
    selectedHotelTypes.length === hotelTypesAmount
  );
}

function cleanRecipe(recipe: GraphQL.RecipeQL): string {
  let untypedIngredients = recipe.ingredients as (JSONObject | undefined)[];

  untypedIngredients = untypedIngredients?.map((ingredient) => {
    if (ingredient?.ingredientType === IngredientTypeQL.Hotel) {
      return cleanupHotelIngredient(ingredient);
    }

    if (ingredient?.ingredientType === IngredientTypeQL.Transport) {
      return cleanupTransportIngredient(ingredient);
    }

    return ingredient;
  });

  return JSON.stringify({
    ...cleanFalsyValues(recipe, ['ingredients']),
    ingredients: untypedIngredients.filter(isDefined),
  });
}

function cleanupHotelIngredient(
  ingredient: JSONObject
): JSONObject | undefined {
  const cleanedIngredient: JSONObject | undefined = cleanFalsyValues(
    ingredient,
    ['subIngredients']
  );

  if (!cleanedIngredient) {
    return undefined;
  }

  delete cleanedIngredient.maxNumberOfHotels;
  delete cleanedIngredient.includeAlternativeResults;
  delete cleanedIngredient.numberOfSuggestions;

  if (
    ingredient.selectedHotelTypes &&
    Array.isArray(ingredient.selectedHotelTypes) &&
    hasAllHotelTypesSelected(
      ingredient.selectedHotelTypes as SelectedHotelTypeQL[]
    )
  ) {
    delete cleanedIngredient.selectedHotelTypes;
  }

  if (
    (cleanedIngredient.subIngredients as Array<JSONObject>) &&
    (cleanedIngredient.subIngredients as Array<JSONObject>).length > 0
  ) {
    cleanedIngredient.subIngredients = (
      cleanedIngredient.subIngredients as Array<JSONObject>
    ).map((subIngredient: JSONObject) => {
      if (subIngredient.ingredientType === IngredientTypeQL.Ticket) {
        return cleanFalsyValues(subIngredient, ['offset']);
      }
      return subIngredient;
    });
  }

  return cleanedIngredient;
}

function cleanupTransportIngredient(
  ingredient: JSONObject
): JSONObject | undefined {
  const cleanedIngredient: JSONObject | undefined = cleanFalsyValues(
    ingredient,
    ['maxNumberOfStops']
  );

  delete cleanedIngredient?.includeAlternativeResults;

  return cleanedIngredient;
}
